import React, { useEffect, useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   COST STRUCTURE BOARD — CORELIA
   ----------------------------------------------------------------------------
   ZONE B1
   - version connectable au banner et à la sous-page
   - le bandeau haut est externalisé dans CostBanner
   - la sous-page peut :
     1. piloter le filtre actif du board
     2. récupérer les métriques du board pour alimenter le banner
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bg: theme?.colors?.background?.surface || "#ffffff",
  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.panel || "#cfd8dd",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",

  shadowXs: theme?.shadows?.xs || "0 1px 3px rgba(11,39,53,0.08)",
  shadowSm: theme?.shadows?.sm || "0 3px 10px rgba(11,39,53,0.08)",

  radiusLg: theme?.borders?.radius?.lg || "6px",
  radiusXl: theme?.borders?.radius?.xl || "8px",
};

/* ============================================================================
   STYLES PARTAGÉS LOCAUX
============================================================================ */

const S = {
  page: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  workspace: {
    width: "100%",
    flex: "1 1 auto",
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "1fr 3fr",
    gap: 14,
    alignItems: "stretch",
    overflow: "hidden",
    boxSizing: "border-box",
    paddingRight: 2,
    paddingBottom: 2,
  },

  leftColumn: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    boxSizing: "border-box",
    alignSelf: "stretch",
    borderRadius: 3,
    border: `1px solid rgba(214,222,228,0.88)`,
    background: T.bg,
    overflow: "hidden",
    display: "grid",
    gridTemplateRows: "auto auto minmax(0, 1fr)",
    gap: 8,
    padding: 12,
  },

  leftTitle: {
    fontSize: "0.84rem",
    lineHeight: 1.2,
    color: T.corelia,
    fontWeight: 600,
    minWidth: 0,
  },

  leftHead: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "48px 1px minmax(0, 1fr) 36px",
    gap: 8,
    alignItems: "center",
    padding: "0 6px 6px 6px",
    boxSizing: "border-box",
    borderBottom: `1px solid rgba(214,222,228,0.75)`,
    minWidth: 0,
  },

  leftList: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    boxSizing: "border-box",
    paddingRight: 2,
  },

  leftRow: {
    width: "100%",
    minWidth: 0,
    minHeight: 24,
    display: "grid",
    gridTemplateColumns: "48px 1px minmax(0, 1fr) 36px",
    alignItems: "center",
    gap: 8,
    padding: "2px 6px",
    borderRadius: 4,
    cursor: "pointer",
    boxSizing: "border-box",
    background: "transparent",
  },

  leftRowCode: {
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.soft,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  leftRowDivider: {
    width: 1,
    height: 12,
    background: "rgba(14,63,86,0.18)",
    justifySelf: "center",
  },

  leftRowLabel: {
    fontSize: "0.73rem",
    lineHeight: 1.15,
    color: T.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  leftRowCount: {
    fontSize: "0.70rem",
    lineHeight: 1,
    color: T.verySoft,
    textAlign: "right",
    whiteSpace: "nowrap",
    justifySelf: "end",
  },

  rightColumn: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto auto minmax(0, 1fr)",
    gap: 12,
    alignItems: "stretch",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  sectionCard: {
    width: "100%",
    minWidth: 0,
    borderRadius: 3,
    border: `1px solid rgba(214,222,228,0.92)`,
    background: "transparent",
    padding: 14,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    overflow: "hidden",
  },

  stretchSectionCard: {
    height: "100%",
    minHeight: 0,
  },

  sectionHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minWidth: 0,
    flex: "0 0 auto",
  },

  sectionTitle: {
    fontSize: "0.84rem",
    lineHeight: 1.2,
    color: T.corelia,
    fontWeight: 600,
    minWidth: 0,
  },

  headerTags: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
    minWidth: 0,
  },

  tag: {
    height: 22,
    padding: "0 8px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid rgba(214,222,228,0.95)`,
    background: "#ffffff",
    fontSize: "0.68rem",
    color: T.soft,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },

  statusTag: {
    height: 22,
    padding: "0 8px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid transparent",
    fontSize: "0.68rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },

  contextGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "92px minmax(220px, 1.6fr) 120px 80px 90px 90px",
    gap: 10,
    alignItems: "center",
    minHeight: 42,
    minWidth: 0,
  },

  contextHead: {
    fontSize: "0.69rem",
    lineHeight: 1,
    color: T.verySoft,
    letterSpacing: "0.02em",
    minWidth: 0,
  },

  contextValue: {
    fontSize: "0.76rem",
    lineHeight: 1.2,
    color: T.text,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  matchedHead: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "92px minmax(240px, 1.7fr) 90px 74px 60px",
    gap: 10,
    padding: "0 2px 6px 2px",
    boxSizing: "border-box",
    borderBottom: `1px solid rgba(214,222,228,0.75)`,
    minWidth: 0,
  },

  matchedList: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },

  matchedRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "92px minmax(240px, 1.7fr) 90px 74px 60px",
    gap: 10,
    alignItems: "center",
    minHeight: 16,
    padding: "0 2px",
    boxSizing: "border-box",
    background: "transparent",
    minWidth: 0,
  },

  rowText: {
    fontSize: "0.74rem",
    lineHeight: 1.2,
    color: T.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  rowSoft: {
    fontSize: "0.73rem",
    lineHeight: 1.2,
    color: T.soft,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },

  coeffBadge: {
    height: 20,
    minWidth: 46,
    padding: "0 8px",
    borderRadius: 999,
    border: `1px solid rgba(14,63,86,0.14)`,
    background: "rgba(14,63,86,0.05)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.69rem",
    color: T.corelia,
    fontWeight: 600,
    justifySelf: "start",
    boxSizing: "border-box",
  },

  removeButton: {
    height: 22,
    minWidth: 44,
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    background: "#ffffff",
    color: T.verySoft,
    fontSize: "0.69rem",
    fontWeight: 500,
    cursor: "pointer",
    boxSizing: "border-box",
  },

  divider: {
    width: "100%",
    height: 1,
    background: "rgba(214,222,228,0.55)",
  },

  searchBlock: {
    width: "100%",
    height: "100%",
    minHeight: 0,
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr)",
    gap: 12,
    minWidth: 0,
    overflow: "hidden",
  },

  searchInputWrap: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
    minWidth: 0,
    flex: "0 0 auto",
  },

  searchInput: {
    width: "100%",
    height: 34,
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    background: "#ffffff",
    padding: "0 14px",
    boxSizing: "border-box",
    outline: "none",
    fontSize: "0.74rem",
    color: T.text,
    minWidth: 0,
  },

  searchNote: {
    fontSize: "0.71rem",
    color: T.verySoft,
    whiteSpace: "nowrap",
  },

  resultsWrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minHeight: 0,
    minWidth: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },

  resultsHead: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "110px minmax(280px, 1.8fr) 100px 70px 70px",
    gap: 10,
    padding: "0 2px 8px 2px",
    boxSizing: "border-box",
    borderBottom: `1px solid rgba(214,222,228,0.75)`,
    minWidth: 0,
    position: "sticky",
    top: 0,
    background: "rgba(11,39,53,0.035)",
    zIndex: 1,
  },

  resultRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "110px minmax(280px, 1.8fr) 100px 70px 70px",
    gap: 10,
    alignItems: "center",
    minHeight: 30,
    padding: "0 2px",
    boxSizing: "border-box",
    background: "transparent",
    minWidth: 0,
  },

  addButton: {
    height: 22,
    minWidth: 54,
    borderRadius: 999,
    border: `1px solid rgba(14,63,86,0.16)`,
    background: "rgba(14,63,86,0.05)",
    color: T.corelia,
    fontSize: "0.69rem",
    fontWeight: 600,
    cursor: "pointer",
    boxSizing: "border-box",
  },
};

const SectionCard = ({ title, headerRight = null, children, style = {} }) => (
  <div style={{ ...S.sectionCard, ...style }}>
    {title || headerRight ? (
      <div style={S.sectionHeader}>
        <div style={S.sectionTitle}>{title}</div>
        {headerRight ? <div style={S.headerTags}>{headerRight}</div> : null}
      </div>
    ) : null}
    {children}
  </div>
);

/* ============================================================================
   MOCK DATA
============================================================================ */

const PACKAGE_ITEMS = [
  {
    code: "2.1",
    name: "Transmission Main",
    type: "Material",
    unit: "m",
    weight: "12%",
    packageName: "Piping Works",
    matchedCount: 2,
  },
  {
    code: "2.2",
    name: "Secondary Branch",
    type: "Material",
    unit: "m",
    weight: "8%",
    packageName: "Piping Works",
    matchedCount: 1,
  },
  {
    code: "2.3",
    name: "Valve Chamber Connection",
    type: "Equipment",
    unit: "unit",
    weight: "4%",
    packageName: "Piping Works",
    matchedCount: 0,
  },
  {
    code: "3.1",
    name: "Trench Excavation",
    type: "Subcontract",
    unit: "m³",
    weight: "10%",
    packageName: "Civil Works",
    matchedCount: 3,
  },
  {
    code: "3.2",
    name: "Pipe Bedding",
    type: "Material",
    unit: "m³",
    weight: "5%",
    packageName: "Civil Works",
    matchedCount: 1,
  },
  {
    code: "3.3",
    name: "Backfilling",
    type: "Subcontract",
    unit: "m³",
    weight: "7%",
    packageName: "Civil Works",
    matchedCount: 2,
  },
  {
    code: "4.1",
    name: "Butterfly Valve DN400",
    type: "Equipment",
    unit: "unit",
    weight: "3%",
    packageName: "Valve Works",
    matchedCount: 1,
  },
  {
    code: "4.2",
    name: "Air Release Valve",
    type: "Equipment",
    unit: "unit",
    weight: "2%",
    packageName: "Valve Works",
    matchedCount: 0,
  },
];

const INITIAL_MATCHED_ARTICLES = [
  {
    code: "CU-PIPE-400-SUP",
    name: "Supply HDPE Pipe DN400 PN16",
    type: "Material",
    unit: "m",
    coefficient: 1.0,
  },
  {
    code: "CU-PIPE-400-INST",
    name: "Installation HDPE Pipe DN400",
    type: "Labor",
    unit: "m",
    coefficient: 1.0,
  },
];

const CATALOG_ARTICLES = [
  {
    code: "CU-PIPE-400-SUP",
    name: "Supply HDPE Pipe DN400 PN16",
    type: "Material",
    unit: "m",
    family: "Pipework",
  },
  {
    code: "CU-PIPE-400-INST",
    name: "Installation HDPE Pipe DN400",
    type: "Labor",
    unit: "m",
    family: "Pipework",
  },
  {
    code: "CU-PIPE-400-JOINT",
    name: "Jointing HDPE Pipe DN400",
    type: "Labor",
    unit: "m",
    family: "Pipework",
  },
  {
    code: "CU-PIPE-400-TRANS",
    name: "Transport and handling for HDPE DN400",
    type: "Logistics",
    unit: "m",
    family: "Pipework",
  },
  {
    code: "CU-VALVE-BF-400",
    name: "Butterfly Valve DN400 supply and installation",
    type: "Equipment",
    unit: "unit",
    family: "Valves",
  },
  {
    code: "CU-EXC-TRENCH",
    name: "Excavation of trench in common soil",
    type: "Subcontract",
    unit: "m³",
    family: "Civil",
  },
  {
    code: "CU-BEDDING-PIPE",
    name: "Sand bedding for buried pipeline",
    type: "Material",
    unit: "m³",
    family: "Civil",
  },
  {
    code: "CU-BACKFILL-PIPE",
    name: "Backfilling and compaction of trench",
    type: "Subcontract",
    unit: "m³",
    family: "Civil",
  },
];

/* ============================================================================
   HELPERS
============================================================================ */

const getStatusStyle = (matchedCount) => {
  if (matchedCount >= 3) {
    return {
      color: T.greenText,
      background: "rgba(22,106,88,0.10)",
      borderColor: "rgba(22,106,88,0.18)",
      label: "Covered",
    };
  }

  if (matchedCount >= 1) {
    return {
      color: T.yellowText,
      background: "rgba(138,74,19,0.10)",
      borderColor: "rgba(138,74,19,0.18)",
      label: "Partial",
    };
  }

  return {
    color: T.greyText,
    background: "rgba(63,93,110,0.08)",
    borderColor: "rgba(63,93,110,0.16)",
    label: "Empty",
  };
};

const getBannerMetrics = ({ matchedCount, catalogCount, coverageLabel }) => {
  return [
    { label: "Matched articles", value: matchedCount, valueColor: T.corelia },
    { label: "Catalog results", value: catalogCount },
    {
      label: "Coverage",
      value: coverageLabel,
      valueColor:
        matchedCount >= 3
          ? T.green
          : matchedCount > 0
          ? T.orangeText
          : T.soft,
    },
  ];
};

const getBannerLegends = () => {
  return [
    { label: "Matched", color: T.corelia },
    { label: "Ready", color: T.green },
    { label: "To complete", color: "#B8621B" },
  ];
};

/* ============================================================================
   MAIN
   ----------------------------------------------------------------------------
   Props de connexion attendues depuis la sous-page :
   - activeFilter         : filtre actif piloté par le banner
   - onBannerDataChange   : callback de remontée des métriques / légendes
============================================================================ */

const CostStructureBoard = ({
  activeFilter = "Matching",
  onBannerDataChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("DN400");
  const [typeFilter, setTypeFilter] = useState("All");
  const [selectedPackageCode, setSelectedPackageCode] = useState(PACKAGE_ITEMS[0].code);
  const [matchedArticles, setMatchedArticles] = useState(INITIAL_MATCHED_ARTICLES);

  const selectedCostItem =
    PACKAGE_ITEMS.find((item) => item.code === selectedPackageCode) || PACKAGE_ITEMS[0];

  /* ==========================================================================
     CALCUL DES RÉSULTATS CATALOGUE
  ========================================================================== */
  const filteredResults = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return CATALOG_ARTICLES.filter((article) => {
      const matchesType = typeFilter === "All" || article.type === typeFilter;

      const matchesSearch =
        normalizedSearch.length === 0 ||
        article.code.toLowerCase().includes(normalizedSearch) ||
        article.name.toLowerCase().includes(normalizedSearch) ||
        article.family.toLowerCase().includes(normalizedSearch) ||
        article.unit.toLowerCase().includes(normalizedSearch);

      return matchesType && matchesSearch;
    });
  }, [searchTerm, typeFilter]);

  /* ==========================================================================
     MÉTRIQUES DÉRIVÉES POUR LE BANNER
  ========================================================================== */
  const matchedCount = matchedArticles.length;
  const catalogCount = filteredResults.length;
  const coverageLabel =
    matchedCount >= 3 ? "Covered" : matchedCount > 0 ? "Partial" : "Empty";

  const statusStyle = getStatusStyle(matchedCount);

  const bannerMetrics = useMemo(
    () =>
      getBannerMetrics({
        matchedCount,
        catalogCount,
        coverageLabel,
      }),
    [matchedCount, catalogCount, coverageLabel]
  );

  const bannerLegends = useMemo(() => getBannerLegends(), []);

  /* ==========================================================================
     REMONTÉE DES DONNÉES VERS LA SOUS-PAGE
  ========================================================================== */
  useEffect(() => {
    if (typeof onBannerDataChange !== "function") return;

    onBannerDataChange({
      metrics: bannerMetrics,
      legends: bannerLegends,
      filterOptions: ["Matching", "Coverage", "Catalog"],
    });
  }, [bannerMetrics, bannerLegends, onBannerDataChange]);

  /* ==========================================================================
     FILTRAGE STRUCTUREL PILOTÉ PAR LE BANNER
  ========================================================================== */
  const showContextSection =
    activeFilter === "Matching" || activeFilter === "Coverage";

  const showMatchedSection =
    activeFilter === "Matching" || activeFilter === "Coverage";

  const showCatalogSection =
    activeFilter === "Matching" || activeFilter === "Catalog";

  /* ==========================================================================
     ACTIONS MÉTIER
  ========================================================================== */
  const handleAddArticle = (article) => {
    const alreadyExists = matchedArticles.some((item) => item.code === article.code);
    if (alreadyExists) return;

    setMatchedArticles((prev) => [
      ...prev,
      {
        ...article,
        coefficient: 1.0,
      },
    ]);
  };

  const handleRemoveArticle = (code) => {
    setMatchedArticles((prev) => prev.filter((item) => item.code !== code));
  };

  return (
    <div style={S.page}>
      <div style={S.workspace}>
        <div style={S.leftColumn}>
          <div style={S.leftTitle}>Packages</div>

          <div style={S.leftHead}>
            <div style={S.contextHead}>Code</div>
            <div />
            <div style={S.contextHead}>Package</div>
            <div style={{ ...S.contextHead, textAlign: "right" }}>#</div>
          </div>

          <div style={S.leftList}>
            {PACKAGE_ITEMS.map((item) => {
              const isActive = item.code === selectedPackageCode;

              return (
                <div
                  key={item.code}
                  onClick={() => setSelectedPackageCode(item.code)}
                  style={{
                    ...S.leftRow,
                    background: isActive ? "rgba(14,63,86,0.06)" : "transparent",
                  }}
                >
                  <div
                    style={{
                      ...S.leftRowCode,
                      color: isActive ? T.corelia : T.soft,
                    }}
                  >
                    {item.code}
                  </div>

                  <div style={S.leftRowDivider} />

                  <div
                    style={{
                      ...S.leftRowLabel,
                      color: isActive ? T.corelia : T.text,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    {item.name}
                  </div>

                  <div
                    style={{
                      ...S.leftRowCount,
                      color: isActive ? T.corelia : T.verySoft,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    {item.matchedCount}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={S.rightColumn}>
          {showContextSection && (
            <SectionCard
              title="Contexte du package"
              style={{
                border: "none",
                padding: 0,
                background: "transparent",
              }}
            >
              <div style={S.contextGrid}>
                <div style={S.contextHead}>Code</div>
                <div style={S.contextHead}>Package item</div>
                <div style={S.contextHead}>Cost type</div>
                <div style={S.contextHead}>Unit</div>
                <div style={S.contextHead}>Weight</div>
                <div style={S.contextHead}>Coverage</div>

                <div style={{ ...S.contextValue, fontWeight: 600 }}>{selectedCostItem.code}</div>
                <div style={{ ...S.contextValue, fontWeight: 600 }}>{selectedCostItem.name}</div>
                <div style={S.contextValue}>{selectedCostItem.type}</div>
                <div style={S.contextValue}>{selectedCostItem.unit}</div>
                <div style={S.contextValue}>{selectedCostItem.weight}</div>
                <div>
                  <div
                    style={{
                      ...S.statusTag,
                      color: statusStyle.color,
                      background: statusStyle.background,
                      borderColor: statusStyle.borderColor,
                      border: `1px solid ${statusStyle.borderColor}`,
                      width: "fit-content",
                      marginLeft: "auto",
                    }}
                  >
                    {statusStyle.label}
                  </div>
                </div>
              </div>
            </SectionCard>
          )}

          {showMatchedSection && (
            <SectionCard
              title=""
              style={{
                border: "none",
                padding: 0,
                background: "transparent",
              }}
            >
              <div style={S.matchedHead}>
                <div style={S.contextHead}>Code</div>
                <div style={S.contextHead}>Article</div>
                <div style={S.contextHead}>Type</div>
                <div style={S.contextHead}>Coeff.</div>
                <div style={S.contextHead}>Action</div>
              </div>

              <div style={S.matchedList}>
                {matchedArticles.map((article, index) => (
                  <React.Fragment key={`${article.code}-${index}`}>
                    <div style={S.matchedRow}>
                      <div style={S.rowSoft}>{article.code}</div>
                      <div style={S.rowText}>{article.name}</div>
                      <div style={S.rowSoft}>{article.type}</div>
                      <div style={S.coeffBadge}>×{article.coefficient.toFixed(2)}</div>
                      <button
                        type="button"
                        style={S.removeButton}
                        onClick={() => handleRemoveArticle(article.code)}
                      >
                        Remove
                      </button>
                    </div>

                    {index < matchedArticles.length - 1 && <div style={S.divider} />}
                  </React.Fragment>
                ))}
              </div>
            </SectionCard>
          )}

          {showCatalogSection && (
            <SectionCard
              title="Recherche et sélection dans le référentiel Corelia"
              headerRight={
                <>
                  <button
                    type="button"
                    onClick={() => setTypeFilter("All")}
                    style={{
                      ...S.tag,
                      cursor: "pointer",
                      border:
                        typeFilter === "All"
                          ? "1px solid rgba(14,63,86,0.18)"
                          : `1px solid ${T.border}`,
                      background:
                        typeFilter === "All" ? "rgba(14,63,86,0.05)" : "#ffffff",
                      color: typeFilter === "All" ? T.corelia : T.soft,
                    }}
                  >
                    All
                  </button>

                  {["Material", "Labor", "Equipment", "Logistics", "Subcontract"].map(
                    (option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setTypeFilter(option)}
                        style={{
                          ...S.tag,
                          cursor: "pointer",
                          border:
                            typeFilter === option
                              ? "1px solid rgba(14,63,86,0.18)"
                              : `1px solid ${T.border}`,
                          background:
                            typeFilter === option ? "rgba(14,63,86,0.05)" : "#ffffff",
                          color: typeFilter === option ? T.corelia : T.soft,
                        }}
                      >
                        {option}
                      </button>
                    )
                  )}
                </>
              }
              style={{
                ...S.stretchSectionCard,
                background: "rgba(11,39,53,0.035)",
                marginTop: 18,
              }}
            >
              <div style={S.searchBlock}>
                <div style={S.searchInputWrap}>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by code, description, family or DN"
                    style={S.searchInput}
                  />
                  <div style={S.searchNote}>
                    {filteredResults.length} result{filteredResults.length > 1 ? "s" : ""}
                  </div>
                </div>

                <div style={S.resultsWrap}>
                  <div style={S.resultsHead}>
                    <div style={S.contextHead}>Code</div>
                    <div style={S.contextHead}>Catalog article</div>
                    <div style={S.contextHead}>Type</div>
                    <div style={S.contextHead}>Unit</div>
                    <div style={S.contextHead}>Action</div>
                  </div>

                  {filteredResults.map((article, index) => {
                    const isAlreadyMatched = matchedArticles.some(
                      (item) => item.code === article.code
                    );

                    return (
                      <React.Fragment key={`${article.code}-${index}`}>
                        <div style={S.resultRow}>
                          <div style={S.rowSoft}>{article.code}</div>
                          <div style={S.rowText}>{article.name}</div>
                          <div style={S.rowSoft}>{article.type}</div>
                          <div style={S.rowSoft}>{article.unit}</div>

                          <button
                            type="button"
                            style={{
                              ...S.addButton,
                              opacity: isAlreadyMatched ? 0.55 : 1,
                              cursor: isAlreadyMatched ? "default" : "pointer",
                            }}
                            onClick={() => {
                              if (!isAlreadyMatched) handleAddArticle(article);
                            }}
                          >
                            {isAlreadyMatched ? "Added" : "Add"}
                          </button>
                        </div>

                        {index < filteredResults.length - 1 && <div style={S.divider} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostStructureBoard;